import type { PassationContenu } from '../../types';
import { formatDateHeure } from '../demande/format';
import { completeeDepuisLecture, dePrenom, jourCourt, phraseDebutent, resumeDebutent } from './accueil';
import type { Voix } from './SectionsPassation';

/** La séance, pour dire « pour qui, quand, où » dans le panneau. */
export interface SeancePassation {
  jourIso: string; atelierNom: string; horaire: string; lieu: string | null; absentNom: string; remplacantNom: string | null;
}

export type EtatSauvegarde = 'repos' | 'en_cours' | 'fait' | 'erreur';

export interface ActionPassation { libelle: string; onClick: () => void; enCours?: boolean }

/** L'unique action principale de l'onglet (transmettre, ou « J'ai lu la passation ») : le panneau et la barre collante portent la même. */
export function actionPrincipale(contenu: PassationContenu, voix: Voix, seance: SeancePassation, h: {
  onTransmettre: () => void; onMarquerLu: () => void; lectureEnCours: boolean;
}): ActionPassation | null {
  const d = contenu.droits;
  if (d.transmettre) return { libelle: voix.vous ? 'J’ai tout transmis' : `Transmettre à la place ${dePrenom(seance.absentNom)}`, onClick: h.onTransmettre };
  // Une relecture (la passation a été complétée depuis la première lecture) : « J'ai relu la passation ».
  if (d.marquerLu) return { libelle: contenu.lue_at ? 'J’ai relu la passation' : 'J’ai lu la passation', onClick: h.onMarquerLu, enCours: h.lectureEnCours };
  return null;
}

const Tache = ({ fait, lecture, onClick, children }: { fait?: boolean; lecture?: boolean; onClick?: () => void; children: React.ReactNode }) => {
  const contenu = (
    <>
      <span className="pg-rond" aria-hidden="true">{fait ? '✓' : ''}</span>
      <span><span className="sr-only">{fait ? 'Fait : ' : 'À faire : '}</span>{children}</span>
    </>
  );
  return (
    <li>
      {lecture
        ? <span className={`pg-tache lecture-seule${fait ? ' fait' : ''}`}>{contenu}</span>
        : <button type="button" className={`pg-tache${fait ? ' fait' : ''}`} onClick={onClick}>{contenu}</button>}
    </li>
  );
};

/**
 * Panneau « Il reste à faire » (à droite, collant) : où en est la passation et l'unique bouton. Trois voix : l'intervenant·e absent·e (« Il reste à faire »,
 * puis « Tout est prêt ») ; la gestion (« Où en est Alexia », bouton secondaire : transmettre à sa place est l'exception) ; la remplaçante (« En un coup d'œil »).
 * « Qui débute » est une information (un lien), jamais une tâche : une ligne qu'on ne peut pas cocher n'a pas sa place dans une liste à faire.
 */
export function PanneauPassation({ contenu, seance, voix, action, nbDebutent, motsTotal, motsFaits, pratiqueFaite, sauvegarde, erreur, onAller, onReessayer }: {
  contenu: PassationContenu; seance: SeancePassation; voix: Voix; action: ActionPassation | null;
  nbDebutent: number; motsTotal: number; motsFaits: number; pratiqueFaite: boolean; sauvegarde: EtatSauvegarde; erreur: string | null;
  onAller: (sectionId: string) => void; onReessayer: () => void;
}) {
  const d = contenu.droits;
  const transmise = !!contenu.transmise_at;
  const estRemplacante = !!contenu.moi?.est_remplacant && !d.ecrire;
  const pour = seance.remplacantNom ?? voix.prenomRemplacant;
  const etat = sauvegarde === 'en_cours' ? 'Enregistrement…' : sauvegarde === 'fait' ? '✓ Enregistré' : sauvegarde === 'erreur' ? 'Pas enregistré' : '';
  const erreurEtReessai = erreur && (
    <div className="pg-erreur" role="alert">
      <span>{erreur}</span>
      <button type="button" className="btn btn-sm" onClick={onReessayer}>Réessayer</button>
    </div>
  );
  const qui = transmise ? (contenu.transmise_par_vous ? 'par vous ' : contenu.transmise_par_nom ? `par ${contenu.transmise_par_nom} ` : '') : '';
  const complete = completeeDepuisLecture(contenu.lue_at, contenu.modifie_at);
  const bouton = (secondaire = false) => action && (
    <button type="button" className={`btn ${secondaire ? '' : 'btn-accent'}`} disabled={action.enCours} onClick={action.onClick}>
      {action.enCours ? <span className="spinner spinner-sm" role="status" aria-label="Enregistrement en cours" /> : action.libelle}
    </button>
  );

  // ── La remplaçante : un coup d'œil ─────────────────────────────────────────
  if (estRemplacante) {
    const ph = phraseDebutent(nbDebutent);
    return (
      <aside className="pg-panneau" id="pg-panneau" tabIndex={-1} aria-label="Passation : en un coup d’œil">
        <div className="card remp">
          <h3 className="pg-pour">En un coup d’œil</h3>
          <ul className="pg-coup-oeil">
            <li><b>{ph.nombre}</b>{ph.texte}</li>
            <li><b>{motsTotal}</b>participant·e·s attendu·e·s</li>
            <li><b>{jourCourt(seance.jourIso)}</b>{[seance.horaire, seance.lieu].filter(Boolean).join(' · ')}</li>
          </ul>
          <div className="pg-sous" style={{ marginTop: 'var(--s12)' }}>
            {transmise
              ? `Transmise ${qui}le ${formatDateHeure(contenu.transmise_at)}.${contenu.lue_at ? ` Lue le ${formatDateHeure(contenu.lue_at)} : ${voix.prenomAbsent} et la gestion le voient.` : ''}${complete ? ` ${voix.prenomAbsent} a complété la passation depuis votre lecture.` : ''}`
              : 'Passation pas encore transmise.'}
          </div>
          {action && <div style={{ marginTop: 'var(--s12)' }}>{bouton()}</div>}
          {erreurEtReessai}
        </div>
      </aside>
    );
  }

  // ── Après la transmission ──────────────────────────────────────────────────
  // La personne désignée n'a pas encore répondu (DECISIONS §38) : la passation est transmise, mais elle ne peut pas la lire avant de confirmer.
  const enAttenteDeReponse = contenu.moi?.remplacant_en_attente === true;
  if (transmise) {
    return (
      <aside className="pg-panneau" id="pg-panneau" tabIndex={-1} aria-label="Passation : transmise">
        <div className="card fait">
          <h3 className="pg-pour">Pour {pour}</h3>
          <ul className="pg-taches">
            <Tache fait lecture>Transmise {qui}le {formatDateHeure(contenu.transmise_at)}</Tache>
            <Tache fait={!!contenu.lue_at} lecture>
              {contenu.lue_at ? `Lue par ${contenu.lue_par_nom ?? voix.prenomRemplacant} le ${formatDateHeure(contenu.lue_at)}${complete ? ' · complétée depuis' : ''}`
                : enAttenteDeReponse ? `Pas encore lue : ${voix.prenomRemplacant} n’a pas encore confirmé` : `Pas encore lue par ${voix.prenomRemplacant}`}
            </Tache>
          </ul>
          {contenu.transmise_incomplete && <div className="pg-sous">Transmise avec des éléments manquants.</div>}
          {d.ecrire && <div className="pg-sous">{voix.vous ? 'Vous pouvez' : `${voix.prenomAbsent} et la gestion peuvent`} encore compléter : {enAttenteDeReponse ? `${voix.prenomRemplacant} verra la version à jour dès qu’il ou elle aura confirmé.` : `${voix.prenomRemplacant} voit la version à jour.`}</div>}
          {action && <div style={{ marginTop: 'var(--s12)' }}>{bouton()}</div>}
          {d.ecrire && <div className="pg-etat" aria-hidden="true">{etat}</div>}
          {erreurEtReessai}
        </div>
      </aside>
    );
  }

  // ── Avant la transmission ──────────────────────────────────────────────────
  const tout = pratiqueFaite && (motsTotal === 0 || motsFaits === motsTotal);
  const titre = tout ? 'Tout est prêt' : voix.vous ? 'Il reste à faire' : `Où en est ${voix.prenomAbsent}`;
  return (
    <aside className="pg-panneau" id="pg-panneau" tabIndex={-1} aria-label="Passation : il reste à faire">
      <div className="card">
        <h3 className="pg-pour">Pour {pour}</h3>
        {d.ecrire ? (
          <>
            {motsTotal > 0 && (
              <div className="pg-info-debutent">
                {resumeDebutent(nbDebutent)}
                {nbDebutent > 0 && <> · <button type="button" className="pg-lien" onClick={() => onAller('pg-debute')}>voir la liste</button></>}
              </div>
            )}
            <h3 className="pg-surtitre">{titre}</h3>
            <ul className="pg-taches">
              {motsTotal > 0 && <Tache fait={motsFaits === motsTotal} onClick={() => onAller('pg-mots')}>Un mot pour chacun·e ({motsFaits} sur {motsTotal})</Tache>}
              <Tache fait={pratiqueFaite} onClick={() => onAller('pg-pratique')}>Côté pratique</Tache>
            </ul>
            {bouton(!voix.vous)}
            {!voix.vous && action && <div className="pg-sous pg-sous-bouton">Seulement si {voix.prenomAbsent} ne peut pas le faire.</div>}
            {voix.vous && <div className="pg-sous pg-sous-bouton">{contenu.moi?.remplacant_en_attente
              ? `${voix.prenomRemplacant} sera prévenu·e dès qu’il ou elle aura confirmé, et pourra alors tout lire. Vous pourrez encore compléter après.`
              : `${voix.prenomRemplacant} sera prévenu·e et pourra tout lire. Vous pourrez encore compléter après.`}</div>}
            {!voix.vous && (
              <div className="pg-sous">{contenu.modifie_at ? `Dernière modification : ${formatDateHeure(contenu.modifie_at)}` : `${voix.prenomAbsent} n’a pas encore commencé.`}</div>
            )}
            <div className="pg-etat" aria-hidden="true">{etat}</div>
            {erreurEtReessai}
          </>
        ) : (
          <div className="pg-sous" style={{ marginTop: 'var(--s12)' }}>Passation en préparation par {voix.prenomAbsent}.</div>
        )}
      </div>
    </aside>
  );
}

/** Barre collante en bas de l'écran quand le panneau passe sous le contenu : le même bouton, et l'état d'enregistrement (avec « Réessayer »). */
export function BarreCollante({ action, texte, erreur, onReessayer }: { action: ActionPassation | null; texte: string; erreur: boolean; onReessayer: () => void }) {
  if (!action) return null;
  return (
    <div className="pg-collant">
      <div className="pg-collant-txt">{erreur ? 'Pas enregistré — votre texte reste à l’écran.' : texte}</div>
      {erreur && <button type="button" className="btn btn-sm" onClick={onReessayer}>Réessayer</button>}
      <button type="button" className="btn btn-accent" disabled={action.enCours} onClick={action.onClick}>{action.libelle}</button>
    </div>
  );
}
